class LCSpinWheel extends ModalDialog {
  constructor() {
    super();
    this.engagementDelay = Math.max(Number(this.dataset.delay) || 20, 10) * 1000;
    this.scrollThreshold = Number(this.dataset.scrollThreshold) || 70;
    this.exposurePercent = Number(this.dataset.exposurePercent) || 30;
    this.dismissedDays = Number(this.dataset.dismissedDays) || 14;
    this.dismissedCookie = 'lc-spin-wheel-dismissed-v2';
    this.subscribedCookie = 'lc-newsletter-subscribed-v3';
    this.sessionShownKey = 'lc-spin-wheel-shown-v2';
    this.sessionExposureKey = 'lc-spin-wheel-exposure-v2';
    this.sessionProductsKey = 'lc-spin-wheel-products-v2';
    this.claimTokenKey = 'lc-spin-wheel-claim-token-v2';
    this.resultKey = 'lc-spin-wheel-result-v2';
    this.form = this.querySelector('.lc-spin-wheel__form');
    this.entryPanel = this.querySelector('[data-spin-entry]');
    this.resultPanel = this.querySelector('[data-spin-result]');
    this.resultHeading = this.querySelector('[data-spin-result-heading]');
    this.codeInput = this.querySelector('[data-spin-code]');
    this.expiryText = this.querySelector('[data-spin-expiry]');
    this.applyLink = this.querySelector('[data-spin-apply]');
    this.copyButton = this.querySelector('[data-spin-copy]');
    this.errorMessage = this.querySelector('[data-spin-error]');
    this.status = this.querySelector('[data-spin-status]');
    this.disc = this.querySelector('[data-spin-disc]');
    this.submitButton = this.querySelector('[data-spin-submit]');
    this.submitLabel = this.querySelector('[data-spin-submit-label]');
    this.declineButton = this.querySelector('[data-spin-decline]');
    this.emailInput = this.querySelector('[data-spin-typewriter]');
    this.placeholderTimer = null;
    this.spinTimer = null;
    this.isBusy = false;
    this.engaged = false;
    this.pendingOpen = false;
    this.typewriterStarted = false;
    this.qaMode = new URLSearchParams(window.location.search).get('lc_spin_qa') === '1';

    this.onSubmit = this.onSubmit.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onExitIntent = this.onExitIntent.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onModalClosed = this.onModalClosed.bind(this);
    this.onDecline = this.onDecline.bind(this);
    this.onCopy = this.onCopy.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form?.addEventListener('submit', this.onSubmit);
    this.declineButton?.addEventListener('click', this.onDecline);
    this.copyButton?.addEventListener('click', this.onCopy);
    document.body.addEventListener('modalClosed', this.onModalClosed);

    if (this.dataset.enable !== 'true' && this.dataset.openInDesignMode !== 'true') return;

    this.trackProductView();
    const storedResult = this.getStoredResult();
    if (storedResult) this.renderResult(storedResult, false);

    if (this.dataset.openInDesignMode === 'true' || this.qaMode) {
      window.requestAnimationFrame(() => this.openWhenAvailable(true));
      return;
    }

    if (!this.canOffer(storedResult)) return;

    window.setTimeout(() => {
      this.engaged = true;
    }, this.engagementDelay);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('mouseout', this.onExitIntent);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.isMobile() && this.hasViewedTwoProducts()) {
      window.setTimeout(() => this.openWhenAvailable(), 900);
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.placeholderTimer);
    window.clearTimeout(this.spinTimer);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.declineButton?.removeEventListener('click', this.onDecline);
    this.copyButton?.removeEventListener('click', this.onCopy);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseout', this.onExitIntent);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.body.removeEventListener('modalClosed', this.onModalClosed);
  }

  canOffer(storedResult) {
    if (this.getSession(this.sessionShownKey) === 'true') return false;
    if (!storedResult && this.getCookie(this.dismissedCookie) !== null) return false;
    return Boolean(storedResult || this.isExposedSession());
  }

  isExposedSession() {
    const stored = this.getSession(this.sessionExposureKey);
    if (stored !== null) return stored === 'true';

    const exposed = this.randomUnit() * 100 < this.exposurePercent;
    this.setSession(this.sessionExposureKey, String(exposed));
    return exposed;
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  trackProductView() {
    if (this.dataset.pageType !== 'product' || !this.dataset.productId) return;
    const viewedProducts = this.readSessionJson(this.sessionProductsKey, []);
    if (!Array.isArray(viewedProducts)) return;
    if (!viewedProducts.includes(this.dataset.productId)) {
      viewedProducts.push(this.dataset.productId);
      this.writeSessionJson(this.sessionProductsKey, viewedProducts.slice(-8));
    }
  }

  hasViewedTwoProducts() {
    const viewedProducts = this.readSessionJson(this.sessionProductsKey, []);
    return Array.isArray(viewedProducts) && viewedProducts.length >= 2;
  }

  onScroll() {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;
    const progress = (window.scrollY / scrollableHeight) * 100;
    if (progress >= this.scrollThreshold) this.openWhenAvailable();
  }

  onExitIntent(event) {
    if (this.isMobile() || !this.engaged) return;
    if (!event.relatedTarget && event.clientY <= 10) this.openWhenAvailable();
  }

  onVisibilityChange() {
    if (document.visibilityState === 'visible' && this.pendingOpen) {
      this.openWhenAvailable();
    }
  }

  onModalClosed() {
    if (!this.pendingOpen) return;
    window.setTimeout(() => this.openWhenAvailable(), 180);
  }

  openWhenAvailable(force = false) {
    if (document.visibilityState !== 'visible') {
      this.pendingOpen = true;
      return;
    }
    if (!force && !this.canOffer(this.getStoredResult())) return;

    const anotherModal = document.querySelector(
      'modal-dialog[open], newsletter-popup[open], quick-view[open]'
    );
    if (anotherModal) {
      this.pendingOpen = true;
      return;
    }

    this.pendingOpen = false;
    this.setSession(this.sessionShownKey, 'true');
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseout', this.onExitIntent);
    this.show();
  }

  show(opener) {
    super.show(opener);
    this.setupTypewriter();
    window.requestAnimationFrame(() => {
      this.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
    });
  }

  hide() {
    if (!this.hasAttribute('open')) return;
    if (
      this.dataset.openInDesignMode !== 'true' &&
      !this.qaMode &&
      !this.getStoredResult()
    ) {
      this.setCookie(this.dismissedCookie, 'true', this.dismissedDays);
    }
    super.hide();
  }

  onDecline() {
    this.hide();
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.isBusy || !this.form?.reportValidity()) return;

    const email = this.emailInput?.value.trim();
    if (!email) {
      this.showError(this.dataset.errorInvalid);
      return;
    }

    this.setBusy(true);
    this.showError('');
    try {
      const result = await this.requestClaim(email);
      this.setCookie(this.subscribedCookie, 'true', 365);
      await this.spinToResult(result);
    } catch (error) {
      this.showError(this.messageForError(error));
      this.setBusy(false);
    }
  }

  async requestClaim(email) {
    const claimToken = this.getClaimToken();
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, consent: true, claimToken })
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        throw new Error('SERVICE_UNAVAILABLE');
      }

      if (response.ok && payload?.ok) return payload;
      if (payload?.error === 'PROCESSING' && attempt < 2) {
        await new Promise(resolve => window.setTimeout(resolve, 1800));
        continue;
      }
      throw new Error(payload?.error || 'SERVICE_UNAVAILABLE');
    }
    throw new Error('SERVICE_UNAVAILABLE');
  }

  async spinToResult(result) {
    const percent = Number(result.percent);
    if (![5, 10, 15, 20, 50].includes(percent)) throw new Error('SERVICE_UNAVAILABLE');
    if (!/^LCW(?:5|10|15|20|50)-[A-HJ-NP-Z2-9]{12}$/.test(result.code)) {
      throw new Error('SERVICE_UNAVAILABLE');
    }

    const segments = [5, 10, 5, 15, 5, 10, 5, 20, 10, 50];
    const matchingSegments = segments
      .map((value, index) => ({ value, index }))
      .filter(segment => segment.value === percent);
    const segment = matchingSegments[Math.floor(this.randomUnit() * matchingSegments.length)];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rotation = reducedMotion ? -segment.index * 36 : 2160 - segment.index * 36;
    const duration = reducedMotion ? 80 : 5400;

    this.status.textContent = this.dataset.spinningStatus;
    this.disc.classList.remove('is-spinning');
    this.disc.style.setProperty('--spin-rotation', `${rotation}deg`);
    void this.disc.offsetWidth;
    this.disc.classList.add('is-spinning');
    this.setAttribute('data-spinning', '');

    await new Promise(resolve => {
      this.spinTimer = window.setTimeout(resolve, duration);
    });

    this.removeAttribute('data-spinning');
    this.saveResult(result);
    this.renderResult(result, true);
    this.setBusy(false);
  }

  renderResult(result, announce) {
    const percent = Number(result.percent);
    const expiry = new Date(result.expiresAt);
    if (![5, 10, 15, 20, 50].includes(percent) || Number.isNaN(expiry.getTime())) return;

    this.entryPanel.hidden = true;
    this.resultPanel.hidden = false;
    this.resultHeading.textContent = this.dataset.resultTemplate.replace('[discount]', percent);
    this.codeInput.value = result.code;
    const localizedDate = new Intl.DateTimeFormat(document.documentElement.lang || 'es', {
      dateStyle: 'long'
    }).format(expiry);
    this.expiryText.textContent = this.dataset.expiryTemplate.replace('[date]', localizedDate);
    this.applyLink.href = `/discount/${encodeURIComponent(result.code)}?redirect=${encodeURIComponent(
      this.dataset.cartUrl || '/cart'
    )}`;

    const segmentIndex = [5, 10, 5, 15, 5, 10, 5, 20, 10, 50].indexOf(percent);
    this.disc.classList.remove('is-spinning');
    this.disc.style.transform = `rotate(${-segmentIndex * 36}deg)`;

    if (announce) {
      this.status.textContent = this.dataset.resultStatusTemplate.replace('[discount]', percent);
      this.resultPanel.focus({ preventScroll: true });
      const dialog = this.querySelector('.lc-spin-wheel__dialog');
      if (dialog) dialog.scrollTop = 0;
    }
  }

  async onCopy() {
    const code = this.codeInput?.value;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      this.codeInput.select();
      this.codeInput.setSelectionRange(0, code.length);
      document.execCommand('copy');
    }

    this.copyButton.textContent = this.dataset.copySuccess;
    this.status.textContent = this.dataset.copySuccess;
    window.setTimeout(() => {
      this.copyButton.textContent = this.dataset.copyDefault;
    }, 2200);
  }

  messageForError(error) {
    const code = error instanceof Error ? error.message : 'SERVICE_UNAVAILABLE';
    if (code === 'INVALID_REQUEST') return this.dataset.errorInvalid;
    if (code === 'SUBSCRIPTION_FAILED') return this.dataset.errorSubscription;
    if (code === 'ALREADY_CLAIMED' || code === 'CLAIM_EXPIRED') {
      return this.dataset.errorAlreadyClaimed;
    }
    if (code === 'RATE_LIMITED') return this.dataset.errorRateLimited;
    return this.dataset.errorUnavailable;
  }

  showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message || '';
    this.errorMessage.hidden = !message;
  }

  setBusy(busy) {
    this.isBusy = busy;
    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', String(busy));
    this.submitLabel.textContent = busy ? this.dataset.busyLabel : this.dataset.submitLabel;
  }

  setupTypewriter() {
    if (!this.emailInput || this.typewriterStarted) return;
    this.typewriterStarted = true;
    const example = this.emailInput.dataset.spinTypewriter;
    if (!example) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.emailInput.placeholder = example;
      return;
    }

    let index = 0;
    this.emailInput.placeholder = '';
    this.emailInput.classList.add('is-typing');
    const typeCharacter = () => {
      if (this.emailInput.value || document.activeElement === this.emailInput) {
        this.emailInput.placeholder = example;
        this.emailInput.classList.remove('is-typing');
        return;
      }
      index += 1;
      this.emailInput.placeholder = example.slice(0, index);
      if (index < example.length) {
        this.placeholderTimer = window.setTimeout(typeCharacter, 105);
      } else {
        this.emailInput.classList.remove('is-typing');
      }
    };
    this.placeholderTimer = window.setTimeout(typeCharacter, 280);
    this.emailInput.addEventListener(
      'focus',
      () => {
        window.clearTimeout(this.placeholderTimer);
        this.emailInput.placeholder = example;
        this.emailInput.classList.remove('is-typing');
      },
      { once: true }
    );
  }

  getClaimToken() {
    const existing = this.getLocal(this.claimTokenKey);
    if (/^[A-Za-z0-9_-]{43}$/.test(existing || '')) return existing;
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    const token = window
      .btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/u, '');
    this.setLocal(this.claimTokenKey, token);
    return token;
  }

  saveResult(result) {
    this.setLocal(
      this.resultKey,
      JSON.stringify({ percent: Number(result.percent), code: result.code, expiresAt: result.expiresAt })
    );
  }

  getStoredResult() {
    const raw = this.getLocal(this.resultKey);
    if (!raw) return null;
    try {
      const result = JSON.parse(raw);
      const expiresAt = new Date(result.expiresAt).getTime();
      if (expiresAt <= Date.now()) {
        window.localStorage.removeItem(this.resultKey);
        return null;
      }
      return result;
    } catch (error) {
      return null;
    }
  }

  randomUnit() {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] / 4294967296;
  }

  setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  }

  getCookie(name) {
    const prefix = `${name}=`;
    const match = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : null;
  }

  getSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  setSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  }

  readSessionJson(key, fallback) {
    try {
      return JSON.parse(window.sessionStorage.getItem(key) || JSON.stringify(fallback));
    } catch (error) {
      return fallback;
    }
  }

  writeSessionJson(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      return;
    }
  }

  getLocal(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  setLocal(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  }
}

if (!customElements.get('lc-spin-wheel')) {
  customElements.define('lc-spin-wheel', LCSpinWheel);
}
